import { css } from 'emotion';
import queryString from 'query-string';
import React, { MouseEvent, useState, ReactElement, useRef } from 'react';
import { Link } from 'react-router-dom';
import Field from 'redux-form/lib/Field';
import serverRenderingUtils from '../../../helpers/serverRenderingUtils';
import { useScript } from '../../hooks/useScript';
import { Button } from '../../shared/Button/NewButton';
import { AccessibleErrorContainer } from '../AccessibleErrorContainer';
import { AppleIcon } from '../AppleIcon';
import { FacebookIcon } from '../FacebookIcon';
import { FieldInputWithRedux } from '../FieldInput';
import { GoogleIcon } from '../GoogleIcon';
import OutboundLink from '../OutboundLink';
import { TwitterIcon } from '../TwitterIcon';
import events from './events';
import {
  ButtonWrapper,
  ErrorAlert,
  FieldWrapper,
  ForgotPasswordButton,
  LoginFormWrapper,
  SocialLoginContainer,
  SocialSignInBorderCSS,
} from './styles';

type Props = {
  clickRequestResetPassword: () => void;
  error: string;
  handleSubmit: () => void;
  submitting: boolean;
  showHelpLinks?: boolean;
  showSocialLogin?: boolean;
  feedUrl: string;
  signupUrl: string;
  showSignUp?: boolean;
};

// Global var for the Sign In With Apple API
declare let AppleID: any;

function getAppleAuthError() {
  if (serverRenderingUtils.windowUndefined()) return null;
  const { authappleerror } = queryString.parse(window.location.search);
  return authappleerror;
}

export function Login(props: Props) {
  const {
    error,
    handleSubmit,
    submitting,
    showHelpLinks = true,
    showSocialLogin = true,
    feedUrl,
    signupUrl,
    showSignUp = true,
  } = props;
  const errorTextRef = useRef<HTMLDivElement | null>(null);
  const feedUrlQuery = feedUrl ? `?feedUrl=${encodeURIComponent(feedUrl)}` : '';
  const authTwitterUrl = `/auth/twitter${feedUrlQuery}`;
  const authFacebookUrl = `/login/facebook${feedUrlQuery}`;
  const authGoogleUrl = `/login/google${feedUrlQuery}`;
  const authAppleError = getAppleAuthError();

  const [appleIdHasLoaded, setAppleIdHasLoaded] = useState(false);

  useScript(
    'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
    () => {
      const redirectURI = new URL(
        '/auth/apple/callback',
        serverRenderingUtils.getBaseUrl()
      );
      AppleID.auth.init({
        clientId: 'com.anchorfminc.anchor.siwa',
        scope: 'name email',
        redirectURI: redirectURI.href,
        state: feedUrl ? `{"feedUrlQuery":"${feedUrlQuery}"}` : '',
      });
      setAppleIdHasLoaded(true);
    }
  );

  const handleClickRequestResetPassword = (evt: MouseEvent) => {
    const { clickRequestResetPassword } = props;
    evt.preventDefault();
    clickRequestResetPassword();
  };

  // TODO: better handle errors
  let errorString: string | ReactElement = '';
  if (error) {
    try {
      const errorObj = JSON.parse(error);
      if (errorObj.status === 401) {
        errorString = 'Invalid username or password.';
      } else if (errorObj.status === 403) {
        errorString =
          "This account has been disabled. <a href='https://help.anchor.fm/hc/en-us'>Contact us</a> if you'd like to enable it.";
      } else {
        errorString =
          'There was a problem logging you in. Please try again from this device.';
      }
    } catch (err) {
      errorString = error;
    }
  } else if (authAppleError) {
    errorString =
      'There was a problem logging you in. Please try again from this device.';
  }

  return (
    <LoginFormWrapper>
      <form
        onSubmit={handleSubmit}
        id="LoginForm"
        method="POST"
        noValidate={true}
      >
        <FieldWrapper>
          <Field
            name="email"
            placeholder="Email"
            component={FieldInputWithRedux}
            type="email"
            ariaDescribedBy="login-email-field"
          />
        </FieldWrapper>
        <FieldWrapper>
          <Field
            name="password"
            placeholder="Password"
            component={FieldInputWithRedux}
            type="password"
            ariaDescribedBy="login-password-field"
          />
        </FieldWrapper>
        {errorString && (
          <AccessibleErrorContainer>
            <ErrorAlert>
              <div dangerouslySetInnerHTML={{ __html: errorString }}></div>
            </ErrorAlert>
          </AccessibleErrorContainer>
        )}
        <ButtonWrapper>
          <Button
            className={css`
              width: 100%;
            `}
            isDisabled={submitting}
            type="submit"
            color="purple"
          >
            Log in
          </Button>
        </ButtonWrapper>
      </form>
      {showHelpLinks && (
        <ForgotPasswordButton onClick={handleClickRequestResetPassword}>
          Forgot password?
        </ForgotPasswordButton>
      )}
      {showSocialLogin && (
        <div aria-label="socialLoginOptions">
          <p
            id="socialLoginOptions"
            className={css`
              margin-bottom: 12px;
            `}
          >
            Or log in with:
          </p>
          <SocialLoginContainer>
            <li>
              <OutboundLink
                onClick={events.trackSocialClick}
                to={authFacebookUrl}
                className={css`
                  ${SocialSignInBorderCSS}
                  color: #39579b;
                `}
                aria-label="Sign in with Facebook"
              >
                <FacebookIcon width={18} height={18} />
              </OutboundLink>
            </li>
            <li>
              <OutboundLink
                onClick={events.trackSocialClick}
                to={authGoogleUrl}
                className={css`
                  ${SocialSignInBorderCSS}
                  color: #8c8c8c;
                `}
                aria-label="Sign in with Google"
              >
                <GoogleIcon width={18} height={18} />
              </OutboundLink>
            </li>
            <li>
              <OutboundLink
                onClick={events.trackSocialClick}
                to={authTwitterUrl}
                className={css`
                  ${SocialSignInBorderCSS}
                  color: #50abf1;
                `}
                aria-label="Sign in with Twitter"
              >
                <TwitterIcon width={22} height={18} />
              </OutboundLink>
            </li>
            {appleIdHasLoaded && (
              <li>
                <button
                  onClick={() => {
                    events.trackSocialClick();
                    AppleID.auth.signIn();
                  }}
                  className={SocialSignInBorderCSS}
                  aria-label="Sign in with Apple"
                >
                  <AppleIcon
                    style={{ marginTop: '-4px' }}
                    width={24}
                    height={24}
                  />
                </button>
              </li>
            )}
          </SocialLoginContainer>
        </div>
      )}
      {showHelpLinks && (
        <div
          className={css`
            text-align: center;
          `}
        >
          {showSignUp && (
            <p>
              Don{`'`}t have an account?{' '}
              <Link to={signupUrl || `/signup${feedUrlQuery}`}>Sign up</Link>{' '}
              instead.
            </p>
          )}
        </div>
      )}
    </LoginFormWrapper>
  );
}
